// Podtytuł ogłoszenia: jedna reguła dla zapisu (formularz, walidacja zapisu, API dealerów) i wyświetlania (karty list).
// Bez linków, numerów telefonów, adresów e-mail i niedozwolonych słów; spacje, powtórzone znaki i tekst pisany
// wielkimi literami — normalizowane. CommonJS — moduł importuje też rest-api.
const { DEFAULT_WORDS, findBadWord, findLink, maskWord } = require('../chat/text.js')

const SUBTITLE_MAX = 60
const SEPARATOR = ' / '

// 9 cyfr (razem albo 3-3-3, 2-3-2-2, (2) 3-2-2), opcjonalnie z +48 / 0048; nie część dłuższej liczby
const PHONE_RE = /(?:^|[^\d.,])(?:(?:\+|00)\s?48[\s.-]?)?(?:\d{9}|\d{3}[\s.-]\d{3}[\s.-]\d{3}|\d{2}[\s.-]\d{3}[\s.-]\d{2}[\s.-]\d{2}|\(\d{2}\)\s?\d{3}[\s.-]?\d{2}[\s.-]?\d{2})(?!\d|[.,]\d)/
const EMAIL_RE = /[\p{L}\p{N}._%+-]+\s?(?:@|\(at\)|\[at\])\s?[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)*\.[a-z]{2,}/iu
const CAPS_MIN_LETTERS = 6
const CAPS_SHARE = 0.7
const LETTER = /\p{L}/u
const UPPER = /\p{Lu}/u

// Pierwszy problem z treścią albo null: { code: 'email' | 'link' | 'phone' | 'word', message }.
function subtitleProblem (text, words = DEFAULT_WORDS) {
    const value = String(text || '')
    if (!value.trim()) return null
    if (EMAIL_RE.test(value)) return { code: 'email', message: 'Usuń adres e-mail z podtytułu — kupujący napiszą przez serwis.' }
    const host = findLink(value)
    if (host) return { code: 'link', message: `Usuń adres strony ${host} z podtytułu.` }
    if (PHONE_RE.test(value)) return { code: 'phone', message: 'Usuń numer telefonu z podtytułu — numer podajesz w polu Telefon.' }
    const bad = findBadWord(value, words)
    if (bad) return { code: 'word', message: `Zmień słowo „${maskWord(bad)}” — tego słowa nie można użyć.` }
    return null
}

// Tekst pisany wielkimi literami (ponad 70% liter, co najmniej 6) — zdaniowo; skróty do 3 liter i wyrazy z cyframi
// (ASO, VAT, AMG, FV23, 4X4) bez zmian.
function sentenceCase (text) {
    const letters = [...text].filter(c => LETTER.test(c))
    if (letters.length < CAPS_MIN_LETTERS || letters.filter(c => UPPER.test(c)).length / letters.length <= CAPS_SHARE) return text
    const lower = text.replace(/[\p{L}\p{N}]+/gu, w => (/\d/.test(w) || w.length <= 3 ? w : w.toLocaleLowerCase('pl')))
    return lower.replace(/\p{L}/u, c => c.toLocaleUpperCase('pl'))
}

// Spacje przycięte i pojedyncze; „!!!”, „???” i powtórzone symbole — jeden znak, „....” — „...”; mała litera powtórzona
// 3 i więcej razy, wielka 4 i więcej (bez „VIII”) — jedna; wielkie litery — zdaniowo.
function normalizeSubtitle (text) {
    const value = String(text || '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\.{4,}/g, '...')
        .replace(/([^\p{L}\p{N}\s.])\1+/gu, '$1')
        .replace(/(\p{Ll})\1{2,}/gu, '$1')
        .replace(/(\p{Lu})\1{3,}/gu, '$1')
    return sentenceCase(value)
}

// Do limitu: obcięcie na granicy słowa.
function clip (text, max = SUBTITLE_MAX) {
    if (text.length <= max) return text
    const cut = text.slice(0, max + 1)
    const space = cut.lastIndexOf(' ')
    return (space > max / 2 ? cut.slice(0, space) : text.slice(0, max)).replace(/[\s,.;:/-]+$/, '')
}

const key = (text) => String(text || '').toLocaleLowerCase('pl').replace(/[^\p{L}\p{N}]+/gu, '')

// Pole wersji (data.equipment): formularz zapisuje „wersja silnikowa / podtytuł”, integracje — sam podtytuł albo
// wersję silnikową. Zwraca [wersja silnikowa z początku pola, podtytuł].
function splitEquipment (equipment, trim) {
    const value = String(equipment || '').trim()
    const t = String(trim || '').trim()
    if (t && value.startsWith(t + SEPARATOR)) return [t, value.slice(t.length + SEPARATOR.length).trim()]
    if (t && key(value) === key(t)) return [t, '']
    return ['', value]
}

// Zapis pola wersji: podtytuł po normalizacji, do limitu. Z niedozwoloną treścią — problem, a value bez podtytułu;
// clipped — podtytuł obcięty do limitu.
function prepareEquipment (equipment, trim, words) {
    const [prefix, raw] = splitEquipment(equipment, trim)
    const subtitle = normalizeSubtitle(raw)
    const problem = subtitleProblem(raw, words) || subtitleProblem(subtitle, words)
    const kept = problem ? '' : clip(subtitle)
    return { value: [prefix, kept].filter(Boolean).join(SEPARATOR), problem, clipped: !problem && kept !== subtitle }
}

// Wyświetlanie tekstu od sprzedającego: po normalizacji; z niedozwoloną treścią — ''.
function safeText (text) {
    const raw = String(text || '').trim()
    const value = normalizeSubtitle(raw)
    return value && !subtitleProblem(raw) && !subtitleProblem(value) ? value : ''
}

// Wyświetlanie: podtytuł ogłoszenia (pole wersji bez wersji silnikowej z początku).
function offerSubtitle (offer) {
    const [, raw] = splitEquipment(offer?.data?.equipment, offer?.tracking?.trim?.title)
    return safeText(raw)
}

// Parametr „Wersja” na stronie ogłoszenia: całe pole wersji, podtytuł po normalizacji; z niedozwoloną treścią — ''.
function offerEquipment (offer) {
    const [prefix, raw] = splitEquipment(offer?.data?.equipment, offer?.tracking?.trim?.title)
    if (prefix && subtitleProblem(prefix)) return ''
    if (!raw) return prefix
    const subtitle = safeText(raw)
    return subtitle ? [prefix, subtitle].filter(Boolean).join(SEPARATOR) : ''
}

module.exports = { SUBTITLE_MAX, normalizeSubtitle, offerEquipment, offerSubtitle, prepareEquipment, safeText, splitEquipment, subtitleProblem }
